import { readChatSessionIdentity } from 'src/auth/sessionEvents';
// BroadcastJobPickerDialog (issue #422 — Messenger entry point).
//
// The Broadcasts tab previously only *listed* JOB_BROADCAST threads; there
// was no way to compose a new broadcast from the Messenger (the composer was
// only reachable from the Jobs area's `JobChatPanel`). This dialog supplies
// the missing `jobId`: the admin picks an event, then a job within it, and we
// hand the chosen job to `BroadcastComposer`.
//
// Data flow mirrors the existing event→jobs pattern used by the notification
// sender (`announcement/notifications/index.tsx`) so we reuse known-good
// endpoints rather than inventing one:
//   - `getAllEvents({ page, pageSize })` → event list (`data.data`).
//   - `getAllJobs(eventId)` → shift rows, each carrying a `jobs[]` array of
//     slots. The broadcast `jobId` is the slot's `id` (same value
//     `JobChatPanel` passes to `BroadcastComposer`).

import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import BroadcastComposer from './BroadcastComposer';
import { getAllEvents, getAllJobs } from 'src/helper/backend_helper';

interface EventOption {
  id: number;
  title: string;
}

export interface JobSlotOption {
  jobId: number;
  shiftId?: number;
  label: string;
}

export function mapBroadcastJobOptions(raw: unknown): JobSlotOption[] {
  const shifts: any[] = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const slots: JobSlotOption[] = [];
  const seen = new Set<number>();

  shifts.forEach((shift: any) => {
    (shift?.jobs ?? []).forEach((slot: any) => {
      // GET /jobs/event/:id currently nests the Job row under `job`. Keep the
      // former top-level shape as a fallback so older API deployments remain
      // usable during the staging handoff.
      const jobId = Number(slot?.job?.id ?? slot?.id);
      if (!Number.isFinite(jobId) || jobId <= 0 || seen.has(jobId)) return;
      seen.add(jobId);

      const position =
        slot?.job?.position ??
        slot?.position?.name ??
        slot?.user?.Position?.name ??
        shift?.position ??
        'Job';
      const shiftId =
        slot?.job?.shiftId ??
        slot?.shift?.id ??
        slot?.shiftId ??
        shift?.id ??
        undefined;
      const shiftLabel = shiftId ? ` · Shift ${shiftId}` : '';

      slots.push({
        jobId,
        shiftId,
        label: `${position} (Job ${jobId})${shiftLabel}`,
      });
    });
  });

  return slots;
}

interface BroadcastJobPickerDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function BroadcastJobPickerDialog({
  open,
  onClose,
}: BroadcastJobPickerDialogProps) {
  const [selectedEvent, setSelectedEvent] = useState<EventOption | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobSlotOption | null>(null);

  const { data: events = [], isLoading: eventsLoading } = useQuery<EventOption[]>(
    ['broadcast-picker-events', readChatSessionIdentity()],
    async () => {
      const response = await getAllEvents({ page: 1, pageSize: 200 });
      const list = response?.data?.data ?? [];
      return list.map((e: any) => ({
        id: e.id,
        title: e.title ?? `Event #${e.id}`,
      }));
    },
    { enabled: open }
  );

  const { data: jobOptions = [], isLoading: jobsLoading } = useQuery<
    JobSlotOption[]
  >(
    ['broadcast-picker-jobs', readChatSessionIdentity(), selectedEvent?.id],
    async () => {
      const response = await getAllJobs(selectedEvent?.id as number);
      const raw = response?.data?.data;
      return mapBroadcastJobOptions(raw);
    },
    { enabled: open && !!selectedEvent?.id }
  );

  const handleClose = () => {
    setSelectedEvent(null);
    setSelectedJob(null);
    onClose();
  };

  // Once a job is chosen we hand off to the broadcast composer. The picker
  // dialog stays mounted underneath so closing the composer returns here.
  const composerJobId = useMemo(() => selectedJob?.jobId, [selectedJob]);

  return (
    <>
      <Dialog
        open={open && !composerJobId}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New broadcast</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }} data-testid="broadcast-job-picker">
            <Typography variant="body2" color="text.secondary">
              Pick the event and job the broadcast should target.
            </Typography>
            <Autocomplete
              options={events}
              loading={eventsLoading}
              value={selectedEvent}
              onChange={(_e, next) => {
                setSelectedEvent(next);
                setSelectedJob(null);
              }}
              getOptionLabel={(o) => o.title}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event"
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'broadcast-picker-event',
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {eventsLoading ? (
                          <CircularProgress size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              options={jobOptions}
              loading={jobsLoading}
              disabled={!selectedEvent}
              value={selectedJob}
              onChange={(_e, next) => setSelectedJob(next)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.jobId === v.jobId}
              noOptionsText={
                selectedEvent ? 'No jobs for this event' : 'Select an event first'
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Job"
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'broadcast-picker-job',
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {jobsLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Choosing a job opens the broadcast composer.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {composerJobId && (
        <BroadcastComposer
          open
          jobId={composerJobId}
          shiftId={selectedJob?.shiftId}
          onClose={handleClose}
        />
      )}
    </>
  );
}
